import { DEFAULT_PAGE_SETTINGS, SECTION_ORDER, getPageSize } from "./elements";
import { unwrapToEjs } from "./richTextHelpers";
import { slotBorderCss } from "./borderHelpers";

/**
 * Shared inline style for every <tr> tag in the generated HTML. Issue #2:
 * keep table rows from being split across pages by the print engine.
 */
const PAGE_BREAK_TR = "page-break-inside: avoid !important; break-inside: avoid !important;";

function camelToKebab(str) {
  return str.replace(/([A-Z])/g, "-$1").toLowerCase();
}

function buildStyle(obj) {
  return Object.entries(obj)
    .filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== "" &&
        v !== "0px" &&
        v !== "transparent" &&
        v !== "none"
    )
    .map(([k, v]) => `${camelToKebab(k)}: ${v}`)
    .join("; ");
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function spacingStyle(p) {
  const parts = [];
  if (p.marginTop) parts.push(`margin-top: ${p.marginTop}px`);
  if (p.marginBottom) parts.push(`margin-bottom: ${p.marginBottom}px`);
  if (p.paddingTop) parts.push(`padding-top: ${p.paddingTop}px`);
  if (p.paddingBottom) parts.push(`padding-bottom: ${p.paddingBottom}px`);
  if (p.paddingLeft) parts.push(`padding-left: ${p.paddingLeft}px`);
  if (p.paddingRight) parts.push(`padding-right: ${p.paddingRight}px`);
  if (p.borderWidth > 0) {
    parts.push(`border: ${p.borderWidth}px ${p.borderStyle || "solid"} ${p.borderColor || "#000000"}`);
    if (p.borderRadius > 0) parts.push(`border-radius: ${p.borderRadius}px`);
  }
  // Inner element border — independent per-side widths applied to this
  // element's own wrapper. Coexists with the global Border above.
  // box-sizing:border-box is always added so border thickness stays inside
  // the layout bounds (no overflow on nested columns/vertical-layouts).
  const eb = p.elementBorder;
  if (eb && (eb.top || eb.bottom || eb.left || eb.right)) {
    const ebStyle = eb.style || "solid";
    const ebColor = eb.color || "#000000";
    if (eb.top)    parts.push(`border-top: ${eb.top}px ${ebStyle} ${ebColor}`);
    if (eb.bottom) parts.push(`border-bottom: ${eb.bottom}px ${ebStyle} ${ebColor}`);
    if (eb.left)   parts.push(`border-left: ${eb.left}px ${ebStyle} ${ebColor}`);
    if (eb.right)  parts.push(`border-right: ${eb.right}px ${ebStyle} ${ebColor}`);
    parts.push(`box-sizing: border-box !important`);
  }
  return parts.join("; ");
}

/**
 * Build cell border CSS string for table cells, honoring:
 *   - per-column borderLeft / borderRight flags
 *   - table-wide allCellBorders preset (all four sides)
 *   - default bottom-only border
 * Used by lineItems + summaryTable EJS generators.
 */
function buildCellBorderCss({ col, borderColor, allCellBorders, includeBottom = true }) {
  const w = "1px";
  const parts = [];
  if (col?.borderLeft || allCellBorders)  parts.push(`border-left: ${w} solid ${borderColor}`);
  if (col?.borderRight || allCellBorders) parts.push(`border-right: ${w} solid ${borderColor}`);
  if (allCellBorders) {
    parts.push(`border-top: ${w} solid ${borderColor}`);
    parts.push(`border-bottom: ${w} solid ${borderColor}`);
  } else if (includeBottom) {
    parts.push(`border-bottom: ${w} solid ${borderColor}`);
  }
  return parts.join("; ");
}

function generateSubRowsEJS(p, context = {}) {
  const subRows = p.subRows || [];
  if (subRows.length === 0) return "";
  const totalCols = (p.columns || []).length;
  const loopVar = p.loopVariable || "items";
  const hasBorder = true;

  return subRows.map((sr, srIdx) => {
    const startCol = Math.min(sr.startCol || 0, totalCols - 1);
    const colSpan = Math.min(sr.colSpan || totalCols, totalCols - startCol);
    const afterCols = totalCols - startCol - colSpan;
    const isLast = srIdx === subRows.length - 1;
    const borderStyle = isLast && hasBorder ? `border-bottom: 1px solid ${p.borderColor};` : "";
    const cellStyle = `padding: ${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px; font-size: ${sr.fontSize || 10}px; color: ${sr.color || "#000000"}; font-style: ${sr.fontStyle || "italic"}; text-align: ${sr.align || "left"}; ${borderStyle}`;
    const emptyStyle = `padding: ${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px; ${borderStyle}`;
    const itemRef = context.paginated ? `${loopVar}[_start + _i]` : `${loopVar}[i]`;
    const labelPrefix = sr.label ? `${escapeHtml(sr.label)}: ` : "";
    const content = `${labelPrefix}<%= ${itemRef}.${sr.key} %>`;

    const cells = [];
    if (startCol > 0) cells.push(`          <td colspan="${startCol}" style="${emptyStyle}"></td>`);
    cells.push(`          <td colspan="${colSpan}" style="${cellStyle}">${content}</td>`);
    if (afterCols > 0) cells.push(`          <td colspan="${afterCols}" style="${emptyStyle}"></td>`);

    return `        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${cells.join("\n")}\n        </tr>`;
  }).join("\n");
}

function elementToEJS(element, context = {}) {
  const { type, props: p } = element;

  switch (type) {
    case "heading": {
      const style = buildStyle({
        fontSize: `${p.fontSize}px`,
        fontWeight: p.fontWeight,
        fontFamily: p.fontFamily,
        textAlign: p.textAlign,
        color: p.color,
        letterSpacing: `${p.letterSpacing || 0}px`,
        textTransform: p.textTransform,
        margin: "0",
      });
      const spacing = spacingStyle(p);
      const content = p.ejsBinding
        ? `<%= ${p.ejsBinding} %>`
        : escapeHtml(p.content);
      return `    <h2 style="${style}${spacing ? "; " + spacing : ""}">${content}</h2>`;
    }

    case "text": {
      const spacing = spacingStyle(p);
      // Rich text — emit the user's HTML inline (creator-trusted content, no sanitization).
      // Unwrap EJS-variable placeholder spans back into real <%= path %> markers.
      // Placeholder shape: <span data-ejs-path="invoice.number">{{invoice.number}}</span>
      if (p.richText) {
        // Two placeholder formats supported (back-compat) — both convert to
        // `<%= path %>` in the exported EJS via the shared helper.
        const html = unwrapToEjs(p.richContent || "");
        return `    <div class="text-block-container" data-component-type="text_block" style="page-break-inside: avoid !important; break-inside: avoid !important; box-sizing: border-box${spacing ? "; " + spacing : ""}">${html}</div>`;
      }
      const style = buildStyle({
        fontSize: `${p.fontSize}px`,
        fontWeight: p.fontWeight,
        fontFamily: p.fontFamily,
        fontStyle: p.fontStyle,
        textAlign: p.textAlign,
        color: p.color,
        lineHeight: String(p.lineHeight),
        margin: "0",
        whiteSpace: "pre-wrap",
      });
      const content = p.ejsBinding
        ? `<%= ${p.ejsBinding} %>`
        : escapeHtml(p.content);
      return `    <p class="text-block-container" data-component-type="text_block" style="${style};page-break-inside: avoid !important;break-inside: avoid !important${spacing ? "; " + spacing : ""}">${content}</p>`;
    }

    case "companyLogo": {
      const spacing = spacingStyle(p);
      const align = p.alignment || "left";
      const src = p.ejsBinding ? `<%= ${p.ejsBinding} %>` : (p.src || "logo.png");
      return `    <div style="text-align: ${align}${spacing ? "; " + spacing : ""}">
      <img src="${src}" alt="Company Logo" style="width: ${p.width}px; height: ${p.height}px; object-fit: contain;" />
    </div>`;
    }

    case "invoiceDetails": {
      const spacing = spacingStyle(p);
      const align = p.alignment || "right";
      const rows = (p.fields || [])
        .map((f) => {
          const val = f.ejsBinding
            ? `<%= ${f.ejsBinding} %>`
            : escapeHtml(f.value);
          return `        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">
          <td style="color: ${p.labelColor}; padding: 2px 12px 2px 0; text-align: ${p.labelAlign || "left"}; width: ${p.labelWidth}px;">${escapeHtml(f.label)}:</td>
          <td style="color: ${p.valueColor}; font-weight: 500; padding: 2px 0; text-align: ${p.valueAlign || "right"};">${val}</td>
        </tr>`;
        })
        .join("\n");
      return `    <div style="text-align: ${align}${spacing ? "; " + spacing : ""}">
      <table style="font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; border-collapse: collapse;${align === "right" ? " margin-left: auto;" : ""}">
        <tbody>
${rows}
        </tbody>
      </table>
    </div>`;
    }

    case "customerInfo": {
      const spacing = spacingStyle(p);
      const titleLine = p.title
        ? `      <p style="font-size: ${p.titleFontSize}px; font-weight: ${p.titleFontWeight}; color: ${p.titleColor}; margin: 0 0 4px 0; font-family: ${p.fontFamily};">${escapeHtml(p.title)}</p>\n`
        : "";
      const fields = (p.fields || [])
        .map((f) => {
          const val = f.ejsBinding
            ? `<%= ${f.ejsBinding} %>`
            : escapeHtml(f.value);
          const labelPart =
            f.label ? `<strong>${escapeHtml(f.label)}: </strong>` : "";
          return `      <p style="font-size: ${p.fontSize}px; color: ${p.color}; margin: 2px 0; font-family: ${p.fontFamily};">${labelPart}${val}</p>`;
        })
        .join("\n");
      return `    <div style="${spacing}">
${titleLine}${fields}
    </div>`;
    }

    case "lineItems": {
      const spacing = spacingStyle(p);
      const cols = p.columns || [];
      const groups = p.columnGroups || [];
      const loopVar = p.loopVariable || "items";
      const idxVar = context.paginated ? "_start + _i" : "i";
      const itemRef = context.paginated ? `${loopVar}[_start + _i]` : `${loopVar}[i]`;
      const hasSubRows = (p.subRows || []).length > 0;
      const allBorders = p.allCellBorders;
      // Body row attribute string. Always includes inline page-break-inside
      // protection (issue #2 spec — every <tr> must carry these properties)
      // and conditionally adds the striped background.
      const stripedBg = p.stripedRows
        ? ` style="${PAGE_BREAK_TR} background-color: <%= _i % 2 === 1 ? '${p.stripedColor}' : 'transparent' %>"`
        : ` style="${PAGE_BREAK_TR}"`;

      // Build column group coverage map
      const coverage = new Array(cols.length).fill(null);
      groups.forEach(g => {
        for (let i = g.startCol; i < g.startCol + g.span && i < cols.length; i++) coverage[i] = g;
      });

      // Generate header rows
      let headersHtml;
      if (groups.length > 0) {
        const row1Cells = cols.map((col, i) => {
          const cellBorders = buildCellBorderCss({ col, borderColor: p.borderColor, allCellBorders: allBorders });
          const g = groups.find(g2 => g2.startCol === i);
          if (g) return `          <th colspan="${g.span}" style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px; text-align: center; ${cellBorders};">${escapeHtml(g.label)}</th>`;
          const inGroup = coverage[i] !== null && groups.find(g2 => g2.startCol !== i && i >= g2.startCol && i < g2.startCol + g2.span);
          if (inGroup) return null;
          return `          <th rowspan="2" style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${p.cellPadding}px; text-align: ${col.align || "left"}; width: ${col.width}; vertical-align: middle; ${cellBorders};">${escapeHtml(col.header)}</th>`;
        }).filter(Boolean).join("\n");
        const row2Cells = cols.map((col, i) => coverage[i] !== null
          ? `          <th style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px; text-align: ${col.align || "left"}; width: ${col.width}; ${buildCellBorderCss({ col, borderColor: p.borderColor, allCellBorders: allBorders })};">${escapeHtml(col.header)}</th>`
          : null
        ).filter(Boolean).join("\n");
        headersHtml = `        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${row1Cells}\n        </tr>\n        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${row2Cells}\n        </tr>`;
      } else {
        headersHtml = `        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${cols.map(c => `          <th style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${p.cellPadding}px; text-align: ${c.align || "left"}; width: ${c.width}; ${buildCellBorderCss({ col: c, borderColor: p.borderColor, allCellBorders: allBorders })};">${escapeHtml(c.header)}</th>`).join("\n")}\n        </tr>`;
      }

      const cells = cols.map((c) => {
        const content = c.key === "index" ? `<%= ${idxVar} + 1 %>` : `<%= ${itemRef}.${c.key} %>`;
        const cb = buildCellBorderCss({ col: c, borderColor: p.borderColor, allCellBorders: allBorders, includeBottom: !hasSubRows });
        return `          <td style="padding: ${p.cellPadding}px; text-align: ${c.align || "left"}; ${cb};">${content}</td>`;
      }).join("\n");

      // Total row
      let totalRowHtml = "";
      if (p.showTotalRow) {
        const totalCols = cols.filter(c => c.showTotal);
        // For each summable column, parse both the numeric part AND any
        // trailing unit suffix (e.g. "100 BAG", "2500 KGS") via regex.
        // We sum the numeric parts and capture the first non-empty unit
        // string so the output reads e.g. "260.00 BAG" instead of "260.00".
        const sumCalcs = totalCols.map(c =>
          `var _sum_${c.key} = 0; var _unit_${c.key} = ""; ` +
          `for(var _kt = 0; _kt < ${loopVar}.length; _kt++) { ` +
            `var _raw_${c.key} = String(${loopVar}[_kt].${c.key}||'').trim(); ` +
            `if (_raw_${c.key}) { ` +
              `var _m_${c.key} = _raw_${c.key}.match(/^([\\d,.\\s-]+)\\s*([a-zA-Z\\s\\/\\.]+)?$/); ` +
              `if (_m_${c.key}) { ` +
                `_sum_${c.key} += parseFloat(_m_${c.key}[1].replace(/[, ]/g,'')) || 0; ` +
                `if (_m_${c.key}[2] && !_unit_${c.key}) { _unit_${c.key} = _m_${c.key}[2].trim(); } ` +
              `} ` +
            `} ` +
          `}`
        ).join(" ");
        const totalCells = cols.map((c, i) => {
          const cb = (allBorders || c.borderLeft || c.borderRight)
            ? buildCellBorderCss({ col: c, borderColor: p.borderColor, allCellBorders: allBorders, includeBottom: !!allBorders })
            : "";
          const style = `padding: ${p.cellPadding}px; text-align: ${c.align || "left"}; border-top: 2px solid ${p.borderColor}; font-weight: bold;${cb ? ` ${cb};` : ""}`;
          if (i === 0) return `          <td style="${style}">${escapeHtml(p.totalLabel == null ? "Total" : p.totalLabel)}</td>`;
          if (c.showTotal) return `          <td style="${style}"><%= _sum_${c.key}.toFixed(2) %><%= _unit_${c.key} ? ' ' + _unit_${c.key} : '' %></td>`;
          return `          <td style="${style}"></td>`;
        }).join("\n");
        totalRowHtml = `\n      <tfoot>\n        <% ${sumCalcs} %>\n        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${totalCells}\n        </tr>\n      </tfoot>`;
      }

      const subRowsHtml = generateSubRowsEJS(p, context);

      if (context.paginated) {
        // The total row must appear ONLY on the final page of the loop —
        // otherwise repeated tables across pages each render their own total.
        const paginatedTotalHtml = totalRowHtml
          ? `<% if (typeof _pg !== 'undefined' && typeof _totalPages !== 'undefined' && _pg === _totalPages - 1) { %>${totalRowHtml}<% } %>`
          : "";
        return `    <table style="width: 100%; table-layout: fixed; font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; border-collapse: collapse${spacing ? "; " + spacing : ""}">
      <thead>
${headersHtml}
      </thead>
      <tbody>
        <% for(var _i = 0; _i < _end - _start; _i++) { %>
        <tr${stripedBg}>
${cells}
        </tr>
${subRowsHtml}        <% } %>
      </tbody>${paginatedTotalHtml}
    </table>`;
      }

      const subRowsHtmlSimple = generateSubRowsEJS(p, { ...context, paginated: false });
      return `    <table style="width: 100%; table-layout: fixed; font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; border-collapse: collapse${spacing ? "; " + spacing : ""}">
      <thead>
${headersHtml}
      </thead>
      <tbody>
        <% for(var i = 0; i < ${loopVar}.length; i++) { %>
        <tr${stripedBg}>
${cells}
        </tr>
${subRowsHtmlSimple}        <% } %>
      </tbody>${totalRowHtml}
    </table>`;
    }

    case "summaryTable": {
      const spacing = spacingStyle(p);
      const cols = p.columns || [];
      const groups = p.columnGroups || [];
      const loopVar = p.loopVariable || "gstSummary";
      const allBorders = p.allCellBorders;
      // Same page-break protection as line items — applied to every body
      // row in the summary table.
      const stripedBg = p.stripedRows
        ? ` style="${PAGE_BREAK_TR} background-color: <%= i % 2 === 1 ? '${p.stripedColor}' : 'transparent' %>"`
        : ` style="${PAGE_BREAK_TR}"`;

      // Coverage map for groups
      const coverage = new Array(cols.length).fill(null);
      groups.forEach(g => {
        for (let i = g.startCol; i < g.startCol + g.span && i < cols.length; i++) coverage[i] = g;
      });

      // Headers (with optional nested groups)
      let headersHtml;
      if (groups.length > 0) {
        const row1 = cols.map((col, i) => {
          const cb = buildCellBorderCss({ col, borderColor: p.borderColor, allCellBorders: allBorders });
          const g = groups.find(g2 => g2.startCol === i);
          if (g) return `          <th colspan="${g.span}" style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px; text-align: center; ${cb};">${escapeHtml(g.label)}</th>`;
          const inGroup = coverage[i] !== null && groups.find(g2 => g2.startCol !== i && i >= g2.startCol && i < g2.startCol + g2.span);
          if (inGroup) return null;
          return `          <th rowspan="2" style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${p.cellPadding}px; text-align: ${col.align || "left"}; width: ${col.width}; vertical-align: middle; ${cb};">${escapeHtml(col.header)}</th>`;
        }).filter(Boolean).join("\n");
        const row2 = cols.map((col, i) => coverage[i] !== null
          ? `          <th style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px; text-align: ${col.align || "left"}; width: ${col.width}; ${buildCellBorderCss({ col, borderColor: p.borderColor, allCellBorders: allBorders })};">${escapeHtml(col.header)}</th>`
          : null
        ).filter(Boolean).join("\n");
        headersHtml = `        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${row1}\n        </tr>\n        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${row2}\n        </tr>`;
      } else {
        headersHtml = `        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${cols.map(c =>
          `          <th style="background-color: ${p.headerBgColor}; color: ${p.headerTextColor}; font-weight: ${p.headerFontWeight}; padding: ${p.cellPadding}px; text-align: ${c.align || "left"}; width: ${c.width}; ${buildCellBorderCss({ col: c, borderColor: p.borderColor, allCellBorders: allBorders })};">${escapeHtml(c.header)}</th>`
        ).join("\n")}\n        </tr>`;
      }

      const cells = cols.map(c =>
        `          <td style="padding: ${p.cellPadding}px; text-align: ${c.align || "left"}; ${buildCellBorderCss({ col: c, borderColor: p.borderColor, allCellBorders: allBorders })};"><%= ${loopVar}[i].${c.key} %></td>`
      ).join("\n");

      let totalRowHtml = "";
      if (p.showTotalRow) {
        const totalCols = cols.filter(c => c.showTotal);
        const sumCalcs = totalCols.map(c => `var _sum_${c.key} = 0; for(var _kt = 0; _kt < ${loopVar}.length; _kt++) { _sum_${c.key} += parseFloat(String(${loopVar}[_kt].${c.key}||'').replace(/[, ]/g,'')) || 0; }`).join(" ");
        const totalCells = cols.map((c, i) => {
          const cb = (allBorders || c.borderLeft || c.borderRight)
            ? buildCellBorderCss({ col: c, borderColor: p.borderColor, allCellBorders: allBorders, includeBottom: !!allBorders })
            : "";
          const style = `padding: ${p.cellPadding}px; text-align: ${c.align || "left"}; border-top: 2px solid ${p.borderColor}; font-weight: bold;${cb ? ` ${cb};` : ""}`;
          if (i === 0) return `          <td style="${style}">${escapeHtml(p.totalLabel == null ? "Total" : p.totalLabel)}</td>`;
          if (c.showTotal) return `          <td style="${style}"><%= _sum_${c.key}.toFixed(2) %></td>`;
          return `          <td style="${style}"></td>`;
        }).join("\n");
        totalRowHtml = `\n      <tfoot>\n        <% ${sumCalcs} %>\n        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">\n${totalCells}\n        </tr>\n      </tfoot>`;
      }

      return `    <table style="width: 100%; table-layout: fixed; font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; border-collapse: collapse${spacing ? "; " + spacing : ""}">
      <thead>
${headersHtml}
      </thead>
      <tbody>
        <% for(var i = 0; i < ${loopVar}.length; i++) { %>
        <tr${stripedBg}>
${cells}
        </tr>
        <% } %>
      </tbody>${totalRowHtml}
    </table>`;
    }

    case "keyValueList": {
      const spacing = spacingStyle(p);
      const items = p.items || [];
      const vGap = p.verticalGap == null ? 4 : p.verticalGap;
      // Optional static heading rendered ABOVE all rows. Emitted as literal
      // HTML text — no EJS evaluation.
      const headingHtml = p.componentHeading
        ? `      <div style="font-weight:bold;font-size:${(p.fontSize || 11) + 1}px;color:${p.color};margin-bottom:4px;width:100%;">${escapeHtml(p.componentHeading)}</div>\n`
        : "";

      // Resolve a single value slot — EJS expression if ejsBinding set, else
      // literal default value text. Returns the inner HTML for one cell.
      const resolveValueHtml = (binding, defaultText) => {
        if (binding) return `<%= ${binding} %>`;
        return escapeHtml(defaultText || "");
      };

      // Per-row vertical margin block reused across patterns
      const rowMargin = `margin-top:${vGap}px;margin-bottom:${vGap}px;`;

      const rows = items.map((it) => {
        const layoutPattern = it.layoutPattern || "single";

        // DUAL ROW — side-by-side left/right pairs
        if (layoutPattern === "dual") {
          const lVal = resolveValueHtml(it.left?.ejsBinding, it.left?.defaultValue);
          const rVal = resolveValueHtml(it.right?.ejsBinding, it.right?.defaultValue);
          return `      <div class="dual-row-wrap" style="display:flex;width:100%;${rowMargin}box-sizing:border-box;">
        <div style="width:50%;display:flex;align-items:center;box-sizing:border-box;padding-right:8px;">
          <div style="width:${p.labelWidth||"40%"};text-align:${p.labelAlign||"left"};font-weight:600 !important;color:${p.labelColor};">${escapeHtml(it.left?.label || "")}</div>
          <div style="flex:1;text-align:${p.valueAlign||"left"};color:${p.valueColor};word-break:break-word;">${lVal}</div>
        </div>
        <div style="width:50%;display:flex;align-items:center;box-sizing:border-box;padding-left:8px;">
          <div style="width:${p.labelWidth||"40%"};text-align:${p.labelAlign||"left"};font-weight:600 !important;color:${p.labelColor};">${escapeHtml(it.right?.label || "")}</div>
          <div style="flex:1;text-align:${p.valueAlign||"left"};color:${p.valueColor};word-break:break-word;">${rVal}</div>
        </div>
      </div>`;
        }

        // SINGLE — Value-Only variant
        const rowType = it.rowType || "label_value";
        if (rowType === "value_only") {
          // Per-row font-size override (empty/0 → inherit global)
          const rowFs = it.rowFontSize ? `font-size:${it.rowFontSize}px;` : "";
          const rowBold = it.rowIsBold ? "font-weight:bold;" : "";
          const valHtml = it.ejsBinding ? `<%= ${it.ejsBinding} %>` : escapeHtml(it.staticText || "");
          return `      <p style="${rowFs}${rowBold}color:${p.valueColor||p.color};${rowMargin}text-align:${p.valueAlign||"left"};width:100%;word-break:break-word;box-sizing:border-box;">${valHtml}</p>`;
        }

        // SINGLE — Label & Value (default — labels are baseline bold per spec)
        const val = resolveValueHtml(it.ejsBinding, it.defaultValue);
        return `      <div style="display:flex;gap:${p.gap||4}px;${rowMargin}line-height:1.4;width:100%;box-sizing:border-box;">
        <div style="width:${p.labelWidth||"40%"};color:${p.labelColor};text-align:${p.labelAlign};font-weight:600 !important;word-break:break-word;">${escapeHtml(it.label || "")}</div>
        <div style="flex:1;color:${p.valueColor};text-align:${p.valueAlign};word-break:break-word;">${val}</div>
      </div>`;
      }).join("\n");
      return `    <div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};width:100%;box-sizing:border-box${spacing ? "; " + spacing : ""}">
${headingHtml}${rows}
    </div>`;
    }

    case "taxDetails": {
      const spacing = spacingStyle(p);
      const divider = `${p.sectionDividerWidth || 1}px solid ${p.sectionDividerColor || "#000000"}`;
      const rowOpen = (bold) => `<div style="display:flex;gap:${p.gap||4}px;padding:${p.rowPadding||4}px 0;line-height:1.4;font-weight:${bold ? "bold" : "normal"};width:100%;box-sizing:border-box;">`;
      const buildItemRow = (it) => `${rowOpen(it.isBold)}
        <div style="width:${p.labelWidth||"60%"};color:${p.labelColor};text-align:${p.labelAlign};word-break:break-word;">${escapeHtml(it.label || "")}</div>
        <div style="flex:1;color:${p.valueColor};text-align:${p.valueAlign};word-break:break-word;">${it.ejsBinding ? `<%= ${it.ejsBinding} %>` : ""}</div>
      </div>`;
      const preHtml = (p.preLoopItems || []).map(buildItemRow).join("\n");
      const postHtml = (p.postLoopItems || []).map(buildItemRow).join("\n");
      // ERPs typically deliver tax_detail as a key→value object (e.g.
      // { "Taxable Amount": "26,81,599.38", "OUTPUT-IGST": "4,82,687.90" }).
      // Iterate with Object.entries so the rendering matches the live data
      // shape without server-side adapters. Falls back gracefully when the
      // variable is missing or not an object.
      const loopHtml = `<% if (typeof ${p.loopVariable} !== 'undefined' && ${p.loopVariable} && typeof ${p.loopVariable} === 'object') { %>
        <% for (const [_tdKey, _tdVal] of Object.entries(${p.loopVariable})) { %>
        <div style="display:flex;gap:${p.gap||4}px;padding:${p.rowPadding||4}px 0;line-height:1.4;width:100%;box-sizing:border-box;">
          <div style="width:${p.labelWidth||"60%"};color:${p.labelColor};text-align:${p.labelAlign};word-break:break-word;"><%= _tdKey %></div>
          <div style="flex:1;color:${p.valueColor};text-align:${p.valueAlign};word-break:break-word;"><%= _tdVal %></div>
        </div>
        <% } %>
      <% } %>`;
      return `    <div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};width:100%;box-sizing:border-box${spacing ? "; " + spacing : ""}">
${preHtml}
      ${preHtml ? `<div style="border-top:${divider};margin:2px 0;"></div>` : ""}
      ${loopHtml}
      ${postHtml ? `<div style="border-top:${divider};margin:2px 0;"></div>` : ""}
${postHtml}
    </div>`;
    }


    case "totals": {
      const spacing = spacingStyle(p);
      const align = p.alignment || "right";
      const rows = (p.rows || [])
        .map((r) => {
          const val = r.ejsBinding
            ? `<%= ${r.ejsBinding} %>`
            : escapeHtml(r.value || "0.00");
          const fw = r.fontWeight === "bold" ? "font-weight: bold; " : "";
          return `        <tr style="page-break-inside: avoid !important; break-inside: avoid !important;">
          <td style="${fw}color: ${p.labelColor}; padding: 4px 20px 4px 0; text-align: ${p.labelAlign || "left"};">${escapeHtml(r.label)}:</td>
          <td style="${fw}color: ${p.valueColor}; text-align: ${p.valueAlign || "right"}; padding: 4px 0;">${val}</td>
        </tr>`;
        })
        .join("\n");
      return `    <div style="text-align: ${align}${spacing ? "; " + spacing : ""}">
      <table style="font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; border-collapse: collapse;${align === "right" ? " margin-left: auto;" : ""} width: ${p.width}px;">
        <tbody>
${rows}
        </tbody>
      </table>
    </div>`;
    }

    case "divider": {
      const spacing = spacingStyle(p);
      return `    <hr style="border: none; border-top: ${p.thickness}px ${p.style} ${p.color}; margin: 0${spacing ? "; " + spacing : ""}" />`;
    }

    case "spacer": {
      const spacing = spacingStyle(p);
      return `    <div style="height: ${p.height}px${spacing ? "; " + spacing : ""}"></div>`;
    }

    case "image": {
      const spacing = spacingStyle(p);
      const src = p.ejsBinding ? `<%= ${p.ejsBinding} %>` : (p.src || "image.png");
      const br = p.borderRadius ? `border-radius: ${p.borderRadius}px; ` : "";
      return `    <div style="text-align: ${p.alignment || "center"}${spacing ? "; " + spacing : ""}">
      <img src="${src}" alt="" style="width: ${p.width}px; height: ${p.height}px; object-fit: contain; ${br}" />
    </div>`;
    }

    case "signature": {
      const spacing = spacingStyle(p);
      return `    <div style="text-align: ${p.alignment || "left"}${spacing ? "; " + spacing : ""}">
      <div style="display: inline-block;">
        <div style="border-bottom: 1px solid ${p.lineColor}; width: ${p.lineWidth}px; margin-bottom: 4px;"></div>
        <p style="font-size: ${p.fontSize}px; color: ${p.color}; margin: 0; font-family: ${p.fontFamily};">${escapeHtml(p.label)}</p>
      </div>
    </div>`;
    }

    case "paymentTerms": {
      const spacing = spacingStyle(p);
      return `    <p style="font-size: ${p.fontSize}px; color: ${p.color}; line-height: ${p.lineHeight}; margin: 0; font-family: ${p.fontFamily}${spacing ? "; " + spacing : ""}">${escapeHtml(p.content)}</p>`;
    }

    case "qrCode": {
      const spacing = spacingStyle(p);
      const src = p.ejsBinding ? `<%= ${p.ejsBinding} %>` : (p.data || "");
      return `    <!-- QR Code: Replace src with your QR generation method -->
    <div style="text-align: ${p.alignment || "right"}${spacing ? "; " + spacing : ""}">
      <img src="${src}" alt="QR Code" style="width: ${p.size}px; height: ${p.size}px;" />
    </div>`;
    }

    case "barcode": {
      const spacing = spacingStyle(p);
      const src = p.ejsBinding ? `<%= ${p.ejsBinding} %>` : (p.data || "");
      return `    <!-- Barcode: Replace src with your barcode generation method -->
    <div style="text-align: ${p.alignment || "center"}${spacing ? "; " + spacing : ""}">
      <img src="${src}" alt="Barcode" style="width: ${p.width}px; height: ${p.height}px;" />
    </div>`;
    }

    case "footer": {
      const spacing = spacingStyle(p);
      const content = p.ejsBinding
        ? `<%= ${p.ejsBinding} %>`
        : escapeHtml(p.content);
      return `    <p style="font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; text-align: ${p.textAlign}; color: ${p.color}; margin: 0${spacing ? "; " + spacing : ""}">${content}</p>`;
    }

    case "twoColumn": {
      const spacing = spacingStyle(p);
      const align = { top: "flex-start", center: "center", bottom: "flex-end" }[p.verticalAlign] || "flex-start";
      const leftHtml = p.leftColumn?.type
        ? elementToEJS({ type: p.leftColumn.type, props: p.leftColumn.props || {} })
        : "";
      const rightHtml = p.rightColumn?.type
        ? elementToEJS({ type: p.rightColumn.type, props: p.rightColumn.props || {} })
        : "";
      const leftBorder = slotBorderCss(p.leftColumn?.elementBorder);
      const rightBorder = slotBorderCss(p.rightColumn?.elementBorder);
      return `    <div class="two-columns-wrapper" data-component-type="two_columns" style="display: flex; gap: ${p.gap}px; align-items: ${align}; box-sizing: border-box !important; page-break-inside: avoid !important; break-inside: avoid !important${spacing ? "; " + spacing : ""}">
      <div style="width: ${p.leftWidth}; flex-shrink: 0; box-sizing: border-box !important;${leftBorder ? " " + leftBorder : ""}">
${leftHtml}
      </div>
      <div style="flex: 1; box-sizing: border-box !important;${rightBorder ? " " + rightBorder : ""}">
${rightHtml}
      </div>
    </div>`;
    }

    case "threeColumn": {
      const spacing = spacingStyle(p);
      const align = { top: "flex-start", center: "center", bottom: "flex-end" }[p.verticalAlign] || "flex-start";
      const leftHtml = p.leftColumn?.type ? elementToEJS({ type: p.leftColumn.type, props: p.leftColumn.props || {} }) : "";
      const midHtml = p.midColumn?.type ? elementToEJS({ type: p.midColumn.type, props: p.midColumn.props || {} }) : "";
      const rightHtml = p.rightColumn?.type ? elementToEJS({ type: p.rightColumn.type, props: p.rightColumn.props || {} }) : "";
      const leftBorder = slotBorderCss(p.leftColumn?.elementBorder);
      const midBorder = slotBorderCss(p.midColumn?.elementBorder);
      const rightBorder = slotBorderCss(p.rightColumn?.elementBorder);
      return `    <div class="three-columns-wrapper" data-component-type="three_columns" style="display: flex; gap: ${p.gap}px; align-items: ${align}; box-sizing: border-box !important; page-break-inside: avoid !important; break-inside: avoid !important${spacing ? "; " + spacing : ""}">
      <div style="width: ${p.leftWidth}; flex-shrink: 0; box-sizing: border-box !important;${leftBorder ? " " + leftBorder : ""}">
${leftHtml}
      </div>
      <div style="width: ${p.midWidth}; flex-shrink: 0; box-sizing: border-box !important;${midBorder ? " " + midBorder : ""}">
${midHtml}
      </div>
      <div style="flex: 1; box-sizing: border-box !important;${rightBorder ? " " + rightBorder : ""}">
${rightHtml}
      </div>
    </div>`;
    }

    case "fourColumn": {
      const spacing = spacingStyle(p);
      const align = { top: "flex-start", center: "center", bottom: "flex-end" }[p.verticalAlign] || "flex-start";
      const c1 = p.col1?.type ? elementToEJS({ type: p.col1.type, props: p.col1.props || {} }) : "";
      const c2 = p.col2?.type ? elementToEJS({ type: p.col2.type, props: p.col2.props || {} }) : "";
      const c3 = p.col3?.type ? elementToEJS({ type: p.col3.type, props: p.col3.props || {} }) : "";
      const c4 = p.col4?.type ? elementToEJS({ type: p.col4.type, props: p.col4.props || {} }) : "";
      const c1Border = slotBorderCss(p.col1?.elementBorder);
      const c2Border = slotBorderCss(p.col2?.elementBorder);
      const c3Border = slotBorderCss(p.col3?.elementBorder);
      const c4Border = slotBorderCss(p.col4?.elementBorder);
      return `    <div class="four-columns-wrapper" data-component-type="four_columns" style="display: flex; gap: ${p.gap}px; align-items: ${align}; box-sizing: border-box !important; page-break-inside: avoid !important; break-inside: avoid !important${spacing ? "; " + spacing : ""}">
      <div style="width: ${p.col1Width}; flex-shrink: 0; box-sizing: border-box !important;${c1Border ? " " + c1Border : ""}">
${c1}
      </div>
      <div style="width: ${p.col2Width}; flex-shrink: 0; box-sizing: border-box !important;${c2Border ? " " + c2Border : ""}">
${c2}
      </div>
      <div style="width: ${p.col3Width}; flex-shrink: 0; box-sizing: border-box !important;${c3Border ? " " + c3Border : ""}">
${c3}
      </div>
      <div style="width: ${p.col4Width || "25%"}; flex-shrink: 0; box-sizing: border-box !important;${c4Border ? " " + c4Border : ""}">
${c4}
      </div>
    </div>`;
    }

    case "verticalLayout": {
      const spacing = spacingStyle(p);
      const rows = p.rows || [];
      // Recursive child compilation. Each row is its own block so
      // page-break-inside: avoid keeps nested components together when the
      // page-print engine decides where to split.
      const rowsHtml = rows.map((row) => {
        const childHtml = row.childType
          ? elementToEJS({ type: row.childType, props: row.childProps || {} }, context)
          : "";
        const rowBorder = slotBorderCss(row.elementBorder);
        return `      <div class="vertical-row-layer" style="width: 100% !important; box-sizing: border-box !important; clear: both; page-break-inside: avoid; break-inside: avoid;${rowBorder ? " " + rowBorder : ""}">
${childHtml}
      </div>`;
      }).join("\n");
      return `    <div class="element-block vertical-layout-wrap" style="width: 100% !important; display: flex; flex-direction: column; gap: ${p.gap || 0}px; box-sizing: border-box !important; page-break-inside: avoid; break-inside: avoid${spacing ? "; " + spacing : ""}">
${rowsHtml}
    </div>`;
    }

    case "pageBreak": {
      return `    <div style="page-break-before: always; break-before: page;"></div>`;
    }

    case "custom": {
      const spacing = spacingStyle(p);
      const fields = p.fields || [];
      const layout = p.layout || "vertical";
      const titleHtml = p.name
        ? `      <p style="font-size: ${p.titleFontSize || 13}px; font-weight: ${p.titleFontWeight || "bold"}; color: ${p.titleColor || "#000000"}; margin: 0 0 4px 0; font-family: ${p.fontFamily || "Arial, sans-serif"};">${escapeHtml(p.name)}</p>\n`
        : "";

      const renderField = (f) => {
        const val = f.ejsBinding
          ? `<%= ${f.ejsBinding} %>`
          : escapeHtml(f.defaultValue || "");
        const labelPart = f.label ? `<strong>${escapeHtml(f.label)}: </strong>` : "";
        if (f.type === "image") {
          const src = f.ejsBinding ? `<%= ${f.ejsBinding} %>` : (f.defaultValue || "image.png");
          return `<img src="${src}" alt="${escapeHtml(f.label || "")}" style="max-width: 200px; height: auto;" />`;
        }
        return `${labelPart}${val}`;
      };

      let fieldsHtml;
      if (layout === "horizontal") {
        fieldsHtml = `      <div style="display: flex; gap: 16px; flex-wrap: wrap;">\n` +
          fields.map((f) =>
            `        <span style="font-size: ${p.fontSize || 12}px; color: ${p.color || "#000000"}; font-family: ${p.fontFamily || "Arial, sans-serif"};">${renderField(f)}</span>`
          ).join("\n") +
          `\n      </div>`;
      } else if (layout === "grid") {
        fieldsHtml = `      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px;">\n` +
          fields.map((f) =>
            `        <p style="font-size: ${p.fontSize || 12}px; color: ${p.color || "#000000"}; margin: 2px 0; font-family: ${p.fontFamily || "Arial, sans-serif"};">${renderField(f)}</p>`
          ).join("\n") +
          `\n      </div>`;
      } else {
        fieldsHtml = fields.map((f) =>
          `      <p style="font-size: ${p.fontSize || 12}px; color: ${p.color || "#000000"}; margin: 2px 0; font-family: ${p.fontFamily || "Arial, sans-serif"};">${renderField(f)}</p>`
        ).join("\n");
      }

      return `    <div style="${spacing}">
${titleHtml}${fieldsHtml}
    </div>`;
    }

    case "pageNumber": {
      const spacing = spacingStyle(p);
      let content;
      if (context.paginated) {
        content = (p.format || "Page {current} of {total}")
          .replace("{current}", "<%= _pg + 1 %>")
          .replace("{total}", "<%= _totalPages %>");
      } else {
        content = (p.format || "Page {current} of {total}")
          .replace("{current}", '<span class="pageNumber"></span>')
          .replace("{total}", '<span class="totalPages"></span>');
      }
      return `    <div style="font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; text-align: ${p.textAlign}; color: ${p.color}${spacing ? "; " + spacing : ""}">${content}</div>`;
    }

    case "carriedForward": {
      const spacing = spacingStyle(p);
      const amountKey = p.amountKey || "amount";
      const loopVar = context.loopVariable || "items";
      return `    <%
      var _cfTotal = 0;
      for(var _k = 0; _k <= Math.min(_end - 1, ${loopVar}.length - 1); _k++) { _cfTotal += parseFloat(${loopVar}[_k].${amountKey}) || 0; }
    %>
    <div style="text-align: ${p.textAlign}; font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; font-weight: ${p.fontWeight}; color: ${p.color}${spacing ? "; " + spacing : ""}">
      ${escapeHtml(p.label)}: <%= _cfTotal.toFixed(2) %>
    </div>`;
    }

    case "broughtForward": {
      const spacing = spacingStyle(p);
      const amountKey = p.amountKey || "amount";
      const loopVar = context.loopVariable || "items";
      return `    <%
      var _bfTotal = 0;
      for(var _k = 0; _k < _start; _k++) { _bfTotal += parseFloat(${loopVar}[_k].${amountKey}) || 0; }
    %>
    <div style="text-align: ${p.textAlign}; font-size: ${p.fontSize}px; font-family: ${p.fontFamily}; font-weight: ${p.fontWeight}; color: ${p.color}${spacing ? "; " + spacing : ""}">
      ${escapeHtml(p.label)}: <%= _bfTotal.toFixed(2) %>
    </div>`;
    }

    default:
      return `    <!-- Unknown element type: ${type} -->`;
  }
}

function calculateMaxLinesPerPage(lineItemsProps, pageSettings, sections) {
  // Manual overrides take precedence — letting power users explicitly set
  // how many physical lines they want per page (e.g. for irregular item
  // descriptions that the height-estimator can't model accurately).
  if (lineItemsProps.maxLinesPerPage && Number(lineItemsProps.maxLinesPerPage) > 0) {
    return Math.max(1, Math.floor(Number(lineItemsProps.maxLinesPerPage)));
  }

  const pageHeightMM = getPageSize(pageSettings.pageSize, pageSettings).heightMM;
  const mTop = pageSettings.marginTop || 20;
  const mBottom = pageSettings.marginBottom || 20;
  const availableMM = pageHeightMM - mTop - mBottom;

  const estimateHeight = (el) => {
    switch (el.type) {
      case "heading": return ((el.props.fontSize || 28) / 3) + 5;
      case "text": return ((el.props.fontSize || 12) / 3) + 3;
      case "companyLogo": return ((el.props.height || 60) / 3.78) + 3;
      case "invoiceDetails": return ((el.props.fields || []).length * 6) + 5;
      case "customerInfo": return ((el.props.fields || []).length * 5) + 8;
      case "twoColumn": return 20;
      case "threeColumn": return 20;
      case "divider": return ((el.props.marginTop || 0) + (el.props.marginBottom || 0)) / 3 + 2;
      case "spacer": return (el.props.height || 24) / 3.78;
      case "totals": return ((el.props.rows || []).length * 6) + 5;
      case "summaryTable": return 20;
      case "signature": return 15;
      case "pageNumber": return 5;
      case "carriedForward":
      case "broughtForward": return 6;
      default: return 8;
    }
  };

  const phMM = (sections.pageHeader || []).reduce((s, el) => s + estimateHeight(el), 0);
  const pfMM = (sections.pageFooter || []).reduce((s, el) => s + estimateHeight(el), 0);
  const bfMM = (sections.body || []).some(el => el.type === "broughtForward") ? 6 : 0;
  const cfMM = (sections.body || []).some(el => el.type === "carriedForward") ? 6 : 0;
  const tableHeaderMM = 8;

  const bodyAvailMM = availableMM - phMM - pfMM - bfMM - cfMM - tableHeaderMM - 5;

  const cellPad = lineItemsProps.cellPadding || 8;
  const fontSize = lineItemsProps.fontSize || 11;
  const rowPx = cellPad * 2 + fontSize + 2;
  const rowMM = rowPx / 3.78;

  return Math.max(5, Math.floor(bodyAvailMM / rowMM)); // returns max line-rows, not items
}

export function generateEJSOutput(input, pageSettings = {}) {
  const ps = { ...DEFAULT_PAGE_SETTINGS, ...pageSettings };

  let sections;
  if (Array.isArray(input)) {
    sections = { reportHeader: [], pageHeader: [], body: input, pageFooter: [], reportFooter: [] };
  } else {
    sections = {
      reportHeader: input.reportHeader || [],
      pageHeader: input.pageHeader || [],
      body: input.body || [],
      pageFooter: input.pageFooter || [],
      reportFooter: input.reportFooter || [],
    };
  }

  const lineItemsEl = sections.body.find(el => el.type === "lineItems");
  const hasAutoPagination = !!lineItemsEl;
  const loopVar = lineItemsEl ? (lineItemsEl.props.loopVariable || "items") : "items";
  const paginatedCtx = { paginated: true, loopVariable: loopVar };

  const sectionPadding = ps.sectionPadding || {};
  function wrapSection(key, html) {
    if (!html) return "";
    const sp = sectionPadding[key] || { top: 0, bottom: 0 };
    const style = [
      sp.top > 0 ? `padding-top: ${sp.top}px` : "",
      sp.bottom > 0 ? `padding-bottom: ${sp.bottom}px` : "",
    ].filter(Boolean).join("; ");
    return style ? `<div style="${style}">${html}</div>` : html;
  }

  const reportHeaderHtml = sections.reportHeader.map(el => elementToEJS(el)).join("\n\n");
  const pageHeaderHtml = sections.pageHeader.map(el => elementToEJS(el, paginatedCtx)).join("\n\n");
  const pageFooterHtml = sections.pageFooter.map(el => elementToEJS(el, paginatedCtx)).join("\n\n");
  const reportFooterHtml = sections.reportFooter.map(el => elementToEJS(el)).join("\n\n");

  let bodyHtml;

  if (hasAutoPagination) {
    const maxLinesPerPage = calculateMaxLinesPerPage(lineItemsEl.props, ps, sections);
    const numSubRows = (lineItemsEl.props.subRows || []).length;
    const baseLinesPerItem = 1 + numSubRows; // each item occupies at least this many physical rows
    const maxCharsPerLine = Number(lineItemsEl.props.maxCharsPerLine) || 0;
    // The wrap-sensitive data key comes from the user via the `Description Key
    // (Wrap Key)` UI input (`paginationWrapKey`). When empty we fall back to
    // auto-detecting a column or sub-row whose key is literally "description"
    // for back-compat with existing templates. Empty value → no wrap-aware
    // pagination (graceful fallback in the runtime EJS).
    const userWrapKey = String(lineItemsEl.props.paginationWrapKey || "").trim();
    const wrapKey = userWrapKey
      || ((lineItemsEl.props.columns || []).find(c => c.key === "description") ? "description" : null)
      || ((lineItemsEl.props.subRows || []).find(s => s.key === "description") ? "description" : null);
    const liIdx = sections.body.indexOf(lineItemsEl);
    const beforeEls = sections.body.slice(0, liIdx).filter(el => el.type !== "broughtForward");
    const afterEls = sections.body.slice(liIdx + 1).filter(el => el.type !== "carriedForward");
    const cfEl = sections.body.find(el => el.type === "carriedForward");
    const bfEl = sections.body.find(el => el.type === "broughtForward");

    const beforeHtml = beforeEls.map(el => elementToEJS(el)).join("\n\n");
    const afterHtml = afterEls.map(el => elementToEJS(el)).join("\n\n");
    const lineItemsHtml = elementToEJS(lineItemsEl, paginatedCtx);
    const cfHtml = cfEl ? elementToEJS(cfEl, paginatedCtx) : "";
    const bfHtml = bfEl ? elementToEJS(bfEl, paginatedCtx) : "";

    bodyHtml = `<%
  /*
   * Dynamic keep-together pagination.
   * For each item, we compute "physical lines" = base lines (1 + subRows) +
   * (when maxCharsPerLine is set and a wrap-sensitive description key exists)
   * additional wrap lines based on the description length. The item is moved
   * to the next page whenever current line count + this item's lines would
   * overflow _maxLines.
   */
  var _baseLines = ${baseLinesPerItem};
  var _maxLines = ${maxLinesPerPage};
  var _maxCharsPerLine = ${maxCharsPerLine || 0};
  var _wrapKey = ${wrapKey ? `"${wrapKey}"` : "null"};
  function _itemLines(it) {
    var lines = _baseLines;
    if (_maxCharsPerLine > 0 && _wrapKey && it && it[_wrapKey]) {
      var txt = String(it[_wrapKey]);
      var extra = Math.max(0, Math.ceil(txt.length / _maxCharsPerLine) - 1);
      lines += extra;
    }
    return lines;
  }
  var _pageStarts = [0];
  var _lineCount = 0;
  for(var _k = 0; _k < ${loopVar}.length; _k++) {
    var _need = _itemLines(${loopVar}[_k]);
    if(_lineCount + _need > _maxLines && _lineCount > 0) {
      _pageStarts.push(_k);
      _lineCount = 0;
    }
    _lineCount += _need;
  }
  if(_pageStarts[_pageStarts.length - 1] !== ${loopVar}.length) { _pageStarts.push(${loopVar}.length); }
  var _totalPages = Math.max(1, _pageStarts.length - 1);
%>
<% for(var _pg = 0; _pg < _totalPages; _pg++) { %>
<% var _start = _pageStarts[_pg]; var _end = _pageStarts[_pg + 1]; %>

  <% if(_pg === 0) { %>
${beforeHtml ? "    <!-- Body elements before line items -->\n" + beforeHtml : ""}
  <% } %>

  <% if(_pg > 0) { %>
${pageHeaderHtml ? "    <!-- Page Header (continuation) -->\n" + pageHeaderHtml : ""}
${bfHtml ? "\n" + bfHtml : ""}
  <% } %>

    <!-- Line Items -->
${lineItemsHtml}

  <% if(_pg < _totalPages - 1) { %>
${cfHtml ? "\n" + cfHtml : ""}
${pageFooterHtml ? "\n    <!-- Page Footer -->\n" + pageFooterHtml : ""}
    <div style="page-break-after: always;"></div>
  <% } %>

  <% if(_pg === _totalPages - 1) { %>
${afterHtml ? "    <!-- Body elements after line items -->\n" + afterHtml : ""}
  <% } %>

<% } %>`;
  } else {
    bodyHtml = sections.body.map(el => elementToEJS(el)).join("\n\n");
  }

  const pageSize = getPageSize(ps.pageSize, ps);
  const css = `    @page {
      size: ${pageSize.cssPageRule};
      margin: 2mm !important;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: ${ps.fontFamily};
      font-size: ${ps.fontSize}px;
      color: #333;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    /* The @page margin already reserves the printable area.
       Do NOT add padding here or the margin gets applied twice. */
    .invoice-container {
      width: 100%;
      max-width: 100%;
      margin: 0;
      padding: 0;
    }
    /* Core Layout Integrity — strictly prevent fracturing of blocks, rows, and wrappers */
    .page-section, .invoice-section, .element-block,
    .vertical-layout-wrap, .vertical-row-layer, .key-value-list-wrap,
    .dual-row-wrap, table, thead,
    tr, td, th, .gst-summary-row, .keep-together,
    .two-columns-wrapper, .three-columns-wrapper, .four-columns-wrapper,
    .text-block-container, .layout-container-row,
    [data-component-type="two_columns"],
    [data-component-type="three_columns"],
    [data-component-type="four_columns"],
    [data-component-type="text_block"] {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    /* Table header/body cells: always wrap, never truncate (was: ellipsis cut off long content) */
    th, td {
      white-space: normal !important;
      word-break: break-word !important;
      overflow-wrap: break-word !important;
    }
    @media print {
      .invoice-container { padding: 0; }
    }
    table { border-collapse: collapse; }
    img { max-width: 100%; }`;

  const allBodyHtml = [
    reportHeaderHtml && `    <!-- ===== Report Header ===== -->\n${wrapSection("reportHeader", reportHeaderHtml)}`,
    !hasAutoPagination && pageHeaderHtml && `    <!-- ===== Page Header ===== -->\n${wrapSection("pageHeader", pageHeaderHtml)}`,
    bodyHtml && `    <!-- ===== Body ===== -->\n${wrapSection("body", bodyHtml)}`,
    !hasAutoPagination && pageFooterHtml && `    <!-- ===== Page Footer ===== -->\n${wrapSection("pageFooter", pageFooterHtml)}`,
    reportFooterHtml && `    <!-- ===== Report Footer ===== -->\n${wrapSection("reportFooter", reportFooterHtml)}`,
  ].filter(Boolean).join("\n\n");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
${css}
  </style>
</head>
<body>
  <div class="invoice-container">
${allBodyHtml}
  </div>
</body>
</html>`;

  return { html, css, bodyHtml: allBodyHtml };
}
